import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Button, Heading, Input, LoadingSpinner, Select, SortableTableHeader, Text } from '../components/ui'
import { ClassificationWithTrend } from '../components/ClassificationTrendIndicator'
import { buildClassificationColorMap, buildClassificationRankMap, normalizeClassificationKey } from '../utils/classificationTrend'
import { dashcorretorService } from '../services/dashcorretor'
import { comercialGestaoService } from '../services/comercialGestao'
import { portaisAtendimentoService } from '../services/portaisAtendimento'
import { logPortaisAtendimento } from '../utils/portaisAtendimentoLogs'
import { usePermission } from '../hooks/usePermissions'
import PortaisAtendimentoCallQueueManageModal from './PortaisAtendimentoCallQueueManageModal'
import type { PortaisAtendimentoBrokerDetailResult, PortaisAtendimentoBrokerRow } from '../types/portaisAtendimento'
import type { BrokerContact } from '../types/comercial'
import type { DashCorretorClassificationRef } from '../types/dashcorretor'
import { PhoneIcon, XMarkIcon } from '@heroicons/react/24/outline'

const ITEMS_PER_PAGE = 20
const MODULE_NAME = 'portais_atendimento'

type NaFilaFilter = '' | 'in_queue' | 'out_queue'

const BROKER_FIELD_LABELS: Record<string, string> = {
  id: 'ID',
  name: 'Nome',
  holder_name: 'Nome do titular',
  classification: 'Categoria',
  classification_previous: 'Categoria anterior',
  state: 'UF',
  city: 'Cidade',
  id_scgp: 'Código SCGP',
  status: 'Status',
  tax_id: 'CNPJ/CPF',
  type: 'Tipo',
  last_access_date: 'Último acesso ao portal',
  last_sale_date: 'Última venda',
  clients_count: 'Quantidade de clientes',
  active_agreements_count: 'Convênios ativos',
  scgp_registered_at: 'Cadastro SCGP',
  scgp_last_update_at: 'Última atualização SCGP',
  created_at: 'Criado em',
}

const DATE_FIELDS = new Set([
  'last_access_date',
  'last_sale_date',
  'scgp_registered_at',
  'scgp_last_update_at',
])

const DATETIME_FIELDS = new Set(['created_at'])

function formatDate(value: string | null | undefined): string {
  if (!value) return '—'
  const raw = String(value).trim()
  if (!raw) return '—'
  const dateOnly = /^\d{4}-\d{2}-\d{2}$/.test(raw)
  const d = dateOnly ? new Date(`${raw}T12:00:00`) : new Date(raw)
  if (Number.isNaN(d.getTime())) return raw
  return d.toLocaleDateString('pt-BR')
}

function formatDateTime(value: string | null | undefined): string {
  if (!value) return '—'
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return String(value)
  return d.toLocaleString('pt-BR')
}

function formatFieldValue(key: string, value: unknown): string {
  if (value == null || value === '') return '—'
  if (DATE_FIELDS.has(key)) return formatDate(String(value))
  if (DATETIME_FIELDS.has(key)) return formatDateTime(String(value))
  if (typeof value === 'boolean') return value ? 'Sim' : 'Não'
  return String(value)
}

function getBrokerFieldLabel(key: string): string {
  return BROKER_FIELD_LABELS[key] ?? key.replace(/_/g, ' ')
}

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message
  return 'Erro inesperado. Tente novamente.'
}

function parseDateSortValue(value: string | null | undefined): number {
  if (!value) return Number.POSITIVE_INFINITY
  const raw = String(value).trim()
  if (!raw) return Number.POSITIVE_INFINITY
  const dateOnly = /^\d{4}-\d{2}-\d{2}$/.test(raw)
  const d = dateOnly ? new Date(`${raw}T12:00:00`) : new Date(raw)
  return Number.isNaN(d.getTime()) ? Number.POSITIVE_INFINITY : d.getTime()
}

const PortaisAtendimentoGestaoFilaTab: React.FC = () => {
  const { hasPermission: canInsert } = usePermission(MODULE_NAME, 'insert')
  const [brokers, setBrokers] = useState<PortaisAtendimentoBrokerRow[]>([])
  const [loading, setLoading] = useState(false)
  const [loadingFilterOptions, setLoadingFilterOptions] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [hasSearched, setHasSearched] = useState(false)
  const [classificationRefs, setClassificationRefs] = useState<DashCorretorClassificationRef[]>([])
  const [availableCategories, setAvailableCategories] = useState<string[]>([])
  const [ufsOptions, setUfsOptions] = useState<string[]>([])
  const [citiesOptions, setCitiesOptions] = useState<string[]>([])
  const [selectedBrokerIds, setSelectedBrokerIds] = useState<Set<string>>(new Set())
  const [currentPage, setCurrentPage] = useState(1)
  const [detailBrokerId, setDetailBrokerId] = useState<string | null>(null)
  const [detailLoading, setDetailLoading] = useState(false)
  const [detailError, setDetailError] = useState<string | null>(null)
  const [detailData, setDetailData] = useState<PortaisAtendimentoBrokerDetailResult | null>(null)
  const [corretorCodeFilter, setCorretorCodeFilter] = useState('')
  const [corretorNameFilter, setCorretorNameFilter] = useState('')
  const [corretorClassificationFilter, setCorretorClassificationFilter] = useState('')
  const [corretorUfFilter, setCorretorUfFilter] = useState('')
  const [corretorCityFilter, setCorretorCityFilter] = useState('')
  const [naFilaFilter, setNaFilaFilter] = useState<NaFilaFilter>('')
  const [queueBrokerIds, setQueueBrokerIds] = useState<Set<string>>(new Set())
  const [manageQueueModalOpen, setManageQueueModalOpen] = useState(false)
  const [addToQueueModalOpen, setAddToQueueModalOpen] = useState(false)
  const [addToQueueReason, setAddToQueueReason] = useState('')
  const [addToQueueError, setAddToQueueError] = useState<string | null>(null)
  const [addToQueueInfo, setAddToQueueInfo] = useState<string | null>(null)
  const [addingToQueue, setAddingToQueue] = useState(false)
  const [sortKey, setSortKey] = useState('name')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc')

  const refreshQueueBrokerIds = useCallback(async () => {
    try {
      const queueIds = await portaisAtendimentoService.listCallQueueBrokerIds()
      setQueueBrokerIds(queueIds)
    } catch (error) {
      console.error('Erro ao carregar fila de ligação:', error)
    }
  }, [])

  const classificationRankMap = useMemo(
    () => buildClassificationRankMap(classificationRefs),
    [classificationRefs]
  )
  const classificationColorMap = useMemo(
    () => buildClassificationColorMap(classificationRefs),
    [classificationRefs]
  )

  const loadFilterOptions = useCallback(async () => {
    setLoadingFilterOptions(true)
    try {
      const [refs, categories, states] = await Promise.all([
        dashcorretorService.listClassificationReferences(),
        comercialGestaoService.listAvailableCategories(),
        portaisAtendimentoService.listActiveBrokerStates(),
      ])
      setClassificationRefs(refs)
      setAvailableCategories(categories)
      setUfsOptions(states)
    } catch (error) {
      console.error('Erro ao carregar opções de filtro:', error)
    } finally {
      setLoadingFilterOptions(false)
    }
  }, [])

  const loadCitiesOptions = useCallback(async (state: string) => {
    try {
      const cities = await portaisAtendimentoService.listActiveBrokerCities(state || undefined)
      setCitiesOptions(cities)
    } catch (error) {
      console.error('Erro ao carregar cidades:', error)
      setCitiesOptions([])
    }
  }, [])

  useEffect(() => {
    void loadFilterOptions()
  }, [loadFilterOptions])

  useEffect(() => {
    void refreshQueueBrokerIds()
  }, [refreshQueueBrokerIds])

  useEffect(() => {
    void loadCitiesOptions(corretorUfFilter)
  }, [corretorUfFilter, loadCitiesOptions])

  useEffect(() => {
    if (corretorCityFilter && !citiesOptions.includes(corretorCityFilter)) {
      setCorretorCityFilter('')
    }
  }, [corretorUfFilter, citiesOptions, corretorCityFilter])

  const hasFiltersActive = useMemo(
    () =>
      !!(
        corretorCodeFilter.trim() ||
        corretorNameFilter.trim() ||
        corretorClassificationFilter ||
        corretorUfFilter ||
        corretorCityFilter ||
        naFilaFilter
      ),
    [
      corretorCodeFilter,
      corretorNameFilter,
      corretorClassificationFilter,
      corretorUfFilter,
      corretorCityFilter,
      naFilaFilter,
    ]
  )

  const handleBuscar = async () => {
    if (!hasFiltersActive) return

    setHasSearched(true)
    setCurrentPage(1)
    setLoadError(null)
    setLoading(true)
    setSelectedBrokerIds(new Set())

    try {
      const [rows, queueIds] = await Promise.all([
        portaisAtendimentoService.searchActiveBrokersForQueue({
          code: corretorCodeFilter,
          name: corretorNameFilter,
          classification: corretorClassificationFilter,
          state: corretorUfFilter,
          city: corretorCityFilter,
        }),
        portaisAtendimentoService.listCallQueueBrokerIds(),
      ])
      setBrokers(rows)
      setQueueBrokerIds(queueIds)
    } catch (error) {
      setLoadError(getErrorMessage(error))
      setBrokers([])
      setQueueBrokerIds(new Set())
    } finally {
      setLoading(false)
    }
  }

  const filteredBrokers = useMemo(() => {
    if (naFilaFilter === 'in_queue') {
      return brokers.filter((b) => queueBrokerIds.has(b.id))
    }
    if (naFilaFilter === 'out_queue') {
      return brokers.filter((b) => !queueBrokerIds.has(b.id))
    }
    return brokers
  }, [brokers, queueBrokerIds, naFilaFilter])

  const toggleSort = useCallback((key: string) => {
    if (sortKey === key) {
      setSortOrder((order) => (order === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortKey(key)
      setSortOrder('asc')
    }
    setCurrentPage(1)
  }, [sortKey])

  const sortedBrokers = useMemo(() => {
    return [...filteredBrokers].sort((a, b) => {
      let aValue: string | number = ''
      let bValue: string | number = ''

      switch (sortKey) {
        case 'classification': {
          const aKey = normalizeClassificationKey(a.classification)
          const bKey = normalizeClassificationKey(b.classification)
          const aRank = classificationRankMap.get(aKey)
          const bRank = classificationRankMap.get(bKey)
          if (aRank !== undefined && bRank !== undefined) {
            aValue = aRank
            bValue = bRank
          } else {
            aValue = (a.classification || '').toLowerCase()
            bValue = (b.classification || '').toLowerCase()
          }
          break
        }
        case 'name':
          aValue = (a.name || '').toLowerCase()
          bValue = (b.name || '').toLowerCase()
          break
        case 'state':
          aValue = (a.state || '').toLowerCase()
          bValue = (b.state || '').toLowerCase()
          break
        case 'city':
          aValue = (a.city || '').toLowerCase()
          bValue = (b.city || '').toLowerCase()
          break
        case 'last_access_date':
          aValue = parseDateSortValue(a.last_access_date)
          bValue = parseDateSortValue(b.last_access_date)
          break
        case 'last_sale_date':
          aValue = parseDateSortValue(a.last_sale_date)
          bValue = parseDateSortValue(b.last_sale_date)
          break
        default:
          aValue = (a.name || '').toLowerCase()
          bValue = (b.name || '').toLowerCase()
      }

      if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1
      if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1
      return 0
    })
  }, [filteredBrokers, sortKey, sortOrder, classificationRankMap])

  const totalPages = Math.max(1, Math.ceil(sortedBrokers.length / ITEMS_PER_PAGE))
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE
  const endIndex = startIndex + ITEMS_PER_PAGE
  const pagedBrokers = sortedBrokers.slice(startIndex, endIndex)

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages)
  }, [currentPage, totalPages])

  const toggleBroker = (brokerId: string) => {
    setSelectedBrokerIds((prev) => {
      const next = new Set(prev)
      if (next.has(brokerId)) next.delete(brokerId)
      else next.add(brokerId)
      return next
    })
  }

  const toggleSelectAllOnPage = () => {
    const visibleIds = filteredBrokers.map((b) => b.id)
    const allSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedBrokerIds.has(id))
    setSelectedBrokerIds((prev) => {
      const next = new Set(prev)
      if (allSelected) {
        visibleIds.forEach((id) => next.delete(id))
      } else {
        visibleIds.forEach((id) => next.add(id))
      }
      return next
    })
  }

  const eligibleToAddBrokerIds = useMemo(
    () => Array.from(selectedBrokerIds).filter((id) => !queueBrokerIds.has(id)),
    [selectedBrokerIds, queueBrokerIds]
  )

  const openAddToQueueModal = () => {
    setAddToQueueError(null)
    setAddToQueueInfo(null)

    if (eligibleToAddBrokerIds.length === 0) {
      setAddToQueueInfo(
        selectedBrokerIds.size === 1
          ? 'O corretor selecionado já está na fila de ligação.'
          : 'Todos os corretores selecionados já estão na fila de ligação.'
      )
      setAddToQueueReason('')
      setAddToQueueModalOpen(true)
      return
    }

    setAddToQueueReason('')
    setAddToQueueModalOpen(true)
  }

  const closeAddToQueueModal = () => {
    if (addingToQueue) return
    setAddToQueueModalOpen(false)
    setAddToQueueReason('')
    setAddToQueueError(null)
    setAddToQueueInfo(null)
  }

  const handleAddToQueue = async () => {
    if (eligibleToAddBrokerIds.length === 0) {
      closeAddToQueueModal()
      return
    }

    const reason = addToQueueReason.trim()
    if (!reason) {
      setAddToQueueError('Informe o motivo da inclusão na fila.')
      return
    }

    setAddingToQueue(true)
    setAddToQueueError(null)

    try {
      const selectedBrokers = brokers.filter((b) => eligibleToAddBrokerIds.includes(b.id))
      const result = await portaisAtendimentoService.addBrokersToCallQueue(eligibleToAddBrokerIds, reason)

      if (result.addedCount > 0) {
        await logPortaisAtendimento('create', `Adicionado(s) ${result.addedCount} corretor(es) à fila de ligação`, {
          entityId: eligibleToAddBrokerIds[0] ?? null,
          metadata: {
            entidade: 'helpdesk_broker_call_queue',
            motivo: reason,
            added_count: result.addedCount,
            broker_ids: eligibleToAddBrokerIds,
            broker_names: selectedBrokers.map((b) => b.name),
            broker_scgp_ids: selectedBrokers.map((b) => b.id_scgp),
            skipped_count: result.skippedCount,
          },
        })
      }

      await refreshQueueBrokerIds()
      setSelectedBrokerIds(new Set())
      closeAddToQueueModal()
    } catch (error) {
      setAddToQueueError(getErrorMessage(error))
    } finally {
      setAddingToQueue(false)
    }
  }

  const openBrokerDetail = async (brokerId: string) => {
    setDetailBrokerId(brokerId)
    setDetailLoading(true)
    setDetailError(null)
    setDetailData(null)
    try {
      const result = await portaisAtendimentoService.getBrokerDetail(brokerId)
      if (!result) {
        setDetailError('Corretor não encontrado.')
        return
      }
      setDetailData(result)
    } catch (error) {
      setDetailError(getErrorMessage(error))
    } finally {
      setDetailLoading(false)
    }
  }

  const closeBrokerDetail = () => {
    setDetailBrokerId(null)
    setDetailData(null)
    setDetailError(null)
    setDetailLoading(false)
  }

  const clearFilters = () => {
    setCorretorCodeFilter('')
    setCorretorNameFilter('')
    setCorretorClassificationFilter('')
    setCorretorUfFilter('')
    setCorretorCityFilter('')
    setNaFilaFilter('')
    setHasSearched(false)
    setBrokers([])
    setLoadError(null)
    setCurrentPage(1)
    setSelectedBrokerIds(new Set())
  }

  const detailBrokerFields = useMemo(() => {
    if (!detailData?.broker) return []
    return Object.entries(detailData.broker)
  }, [detailData])

  return (
    <div className="flex-1 flex flex-col overflow-y-auto">
      <div className="px-6 py-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex flex-wrap items-center justify-between gap-3">
            <Heading as="h2" size="2xl" className="text-gray-900 dark:text-white">
              Corretores ativos
              {hasSearched && !loading && (
                <span className="ml-2 text-base font-normal text-gray-500 dark:text-gray-400">
                  ({naFilaFilter && brokers.length !== filteredBrokers.length
                    ? `${filteredBrokers.length} de ${brokers.length}`
                    : filteredBrokers.length}{' '}
                  {filteredBrokers.length === 1 ? 'corretor' : 'corretores'})
                </span>
              )}
            </Heading>
            <div className="flex flex-wrap items-center gap-3">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setManageQueueModalOpen(true)}
              >
                <PhoneIcon className="h-4 w-4 mr-1.5 inline" aria-hidden />
                Gerenciar fila de ligação
                <span className="ml-2 inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full bg-primary-100 dark:bg-primary-900/40 text-xs font-medium text-primary-800 dark:text-primary-200">
                  {queueBrokerIds.size}
                </span>
              </Button>
            </div>
          </div>

          {loadError && (
            <div className="px-6 py-4 border-b border-red-200 dark:border-red-900/40 bg-red-50 dark:bg-red-950/30">
              <Text size="sm" className="text-red-700 dark:text-red-300">{loadError}</Text>
            </div>
          )}

          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
              <div>
                <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">
                  Código do corretor
                </Text>
                <Input
                  type="text"
                  placeholder="Código SCGP..."
                  value={corretorCodeFilter}
                  onChange={(e) => setCorretorCodeFilter(e.target.value)}
                  className="w-full"
                  disabled={loading}
                />
              </div>
              <div>
                <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">
                  Nome do corretor
                </Text>
                <Input
                  type="text"
                  placeholder="Nome..."
                  value={corretorNameFilter}
                  onChange={(e) => setCorretorNameFilter(e.target.value)}
                  className="w-full"
                  disabled={loading}
                />
              </div>
              <div>
                <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">
                  Categoria
                </Text>
                <Select
                  value={corretorClassificationFilter}
                  onChange={(e) => setCorretorClassificationFilter(e.target.value)}
                  disabled={loading || loadingFilterOptions}
                >
                  <option value="">Todas</option>
                  {availableCategories.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </Select>
              </div>
              <div>
                <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">
                  UF
                </Text>
                <Select
                  value={corretorUfFilter}
                  onChange={(e) => setCorretorUfFilter(e.target.value)}
                  disabled={loading || loadingFilterOptions}
                >
                  <option value="">Todas</option>
                  {ufsOptions.map((uf) => (
                    <option key={uf} value={uf}>
                      {uf}
                    </option>
                  ))}
                </Select>
              </div>
              <div>
                <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">
                  Cidade
                </Text>
                <Select
                  value={corretorCityFilter}
                  onChange={(e) => setCorretorCityFilter(e.target.value)}
                  disabled={loading || loadingFilterOptions}
                >
                  <option value="">Todas</option>
                  {citiesOptions.map((city) => (
                    <option key={city} value={city}>
                      {city}
                    </option>
                  ))}
                </Select>
              </div>
              <div>
                <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">
                  Na fila
                </Text>
                <Select
                  value={naFilaFilter}
                  onChange={(e) => setNaFilaFilter(e.target.value as NaFilaFilter)}
                  disabled={loading}
                >
                  <option value="">Todos</option>
                  <option value="in_queue">Na fila</option>
                  <option value="out_queue">Fora da fila</option>
                </Select>
              </div>
            </div>
            <div className="mt-4 flex flex-wrap justify-end gap-2">
              <Button
                variant="solid"
                size="sm"
                onClick={() => void handleBuscar()}
                disabled={loading || !hasFiltersActive}
                title={!hasFiltersActive ? 'Selecione ao menos um filtro para buscar' : undefined}
              >
                {loading ? 'Buscando...' : 'Buscar'}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={clearFilters}
                disabled={loading || (!hasSearched && !hasFiltersActive)}
              >
                Limpar filtros
              </Button>
            </div>
          </div>

          {selectedBrokerIds.size > 0 && (
            <div className="px-6 py-3 border-b border-gray-200 dark:border-gray-700 flex flex-wrap items-center justify-between gap-3 bg-primary-50/50 dark:bg-primary-950/20">
              <Text size="sm" className="text-gray-700 dark:text-gray-300">
                {selectedBrokerIds.size} selecionado{selectedBrokerIds.size === 1 ? '' : 's'}
                {eligibleToAddBrokerIds.length < selectedBrokerIds.size && (
                  <span className="text-gray-500 dark:text-gray-400">
                    {' '}
                    ({eligibleToAddBrokerIds.length}{' '}
                    {eligibleToAddBrokerIds.length === 1 ? 'pode ser incluído' : 'podem ser incluídos'} na fila)
                  </span>
                )}
              </Text>
              {canInsert && (
                <Button variant="solid" size="sm" onClick={openAddToQueueModal}>
                  <PhoneIcon className="h-4 w-4 mr-1.5 inline" aria-hidden />
                  Adicionar à fila de ligação
                </Button>
              )}
            </div>
          )}

          <div className="overflow-x-auto">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <LoadingSpinner />
              </div>
            ) : (
              <table className="w-full divide-y divide-gray-200 dark:divide-gray-700 min-w-[960px]">
                <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0 z-10">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap w-12">
                      <input
                        type="checkbox"
                        checked={
                          filteredBrokers.length > 0 &&
                          filteredBrokers.every((b) => selectedBrokerIds.has(b.id))
                        }
                        onChange={toggleSelectAllOnPage}
                        aria-label="Selecionar todos os corretores filtrados"
                      />
                    </th>
                    <SortableTableHeader
                      sortKey="classification"
                      currentSort={sortKey}
                      sortOrder={sortOrder}
                      onSort={toggleSort}
                      compact
                      className="px-4 py-3"
                    >
                      Categoria
                    </SortableTableHeader>
                    <SortableTableHeader
                      sortKey="name"
                      currentSort={sortKey}
                      sortOrder={sortOrder}
                      onSort={toggleSort}
                      compact
                      className="px-4 py-3"
                    >
                      Nome
                    </SortableTableHeader>
                    <SortableTableHeader
                      sortKey="state"
                      currentSort={sortKey}
                      sortOrder={sortOrder}
                      onSort={toggleSort}
                      compact
                      className="px-4 py-3"
                    >
                      UF
                    </SortableTableHeader>
                    <SortableTableHeader
                      sortKey="city"
                      currentSort={sortKey}
                      sortOrder={sortOrder}
                      onSort={toggleSort}
                      compact
                      className="px-4 py-3"
                    >
                      Cidade
                    </SortableTableHeader>
                    <SortableTableHeader
                      sortKey="last_access_date"
                      currentSort={sortKey}
                      sortOrder={sortOrder}
                      onSort={toggleSort}
                      compact
                      className="px-4 py-3"
                    >
                      Último acesso ao portal
                    </SortableTableHeader>
                    <SortableTableHeader
                      sortKey="last_sale_date"
                      currentSort={sortKey}
                      sortOrder={sortOrder}
                      onSort={toggleSort}
                      compact
                      className="px-4 py-3"
                    >
                      Última venda
                    </SortableTableHeader>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {!hasSearched ? (
                    <tr>
                      <td colSpan={7} className="px-4 py-10 text-center">
                        <Text size="sm" color="muted">
                          Selecione ao menos um filtro e clique em Buscar para carregar os corretores.
                        </Text>
                      </td>
                    </tr>
                  ) : filteredBrokers.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-4 py-10 text-center">
                        <Text size="sm" color="muted">Nenhum corretor encontrado com os filtros aplicados.</Text>
                      </td>
                    </tr>
                  ) : (
                    pagedBrokers.map((broker) => (
                      <tr
                        key={broker.id}
                        className="hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer"
                        onClick={() => void openBrokerDetail(broker.id)}
                      >
                        <td className="px-4 py-4" onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={selectedBrokerIds.has(broker.id)}
                            onChange={() => toggleBroker(broker.id)}
                            aria-label={`Selecionar ${broker.name}`}
                          />
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-900 dark:text-gray-100 whitespace-nowrap">
                          <ClassificationWithTrend
                            classification={broker.classification}
                            classificationPrevious={broker.classification_previous}
                            rankMap={classificationRankMap}
                            colorMap={classificationColorMap}
                            emptyLabel="—"
                          />
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-900 dark:text-gray-100">
                          <div className="flex flex-wrap items-center gap-2">
                            <span>{broker.name}</span>
                            {queueBrokerIds.has(broker.id) && (
                              <span className="inline-flex rounded-full bg-primary-100 dark:bg-primary-900/40 px-2 py-0.5 text-xs font-medium text-primary-800 dark:text-primary-200">
                                Na fila
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-600 dark:text-gray-300">{broker.state || '—'}</td>
                        <td className="px-4 py-4 text-sm text-gray-600 dark:text-gray-300">{broker.city || '—'}</td>
                        <td className="px-4 py-4 text-sm text-gray-600 dark:text-gray-300 whitespace-nowrap">
                          {formatDate(broker.last_access_date)}
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-600 dark:text-gray-300 whitespace-nowrap">
                          {formatDate(broker.last_sale_date)}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            )}
          </div>

          {totalPages > 1 && !loading && hasSearched && filteredBrokers.length > 0 && (
            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <Text size="sm" className="text-gray-700 dark:text-gray-300">
                  Mostrando {startIndex + 1} a {Math.min(endIndex, filteredBrokers.length)} de {filteredBrokers.length} registros
                </Text>
                <div className="flex items-center space-x-2">
                  <button
                    type="button"
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                  >
                    Anterior
                  </button>
                  <Text size="sm" className="text-gray-600 dark:text-gray-300">
                    Página {currentPage} de {totalPages}
                  </Text>
                  <button
                    type="button"
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                  >
                    Próximo
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {detailBrokerId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={closeBrokerDetail}>
          <div
            className="bg-white dark:bg-gray-900 rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between mb-4 gap-4">
              <div>
                <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white">
                  {detailData?.broker.name ?? 'Corretor'}
                </Heading>
                {detailData?.broker.id_scgp != null && (
                  <Text size="sm" color="muted" className="mt-1">
                    Código SCGP: {detailData.broker.id_scgp}
                  </Text>
                )}
              </div>
              <button
                type="button"
                onClick={closeBrokerDetail}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                aria-label="Fechar"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            {detailLoading ? (
              <div className="flex items-center justify-center py-12">
                <LoadingSpinner />
              </div>
            ) : detailError ? (
              <Text size="sm" className="text-red-600 dark:text-red-400">{detailError}</Text>
            ) : detailData ? (
              <>
                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3 text-sm">
                  {detailBrokerFields.map(([key, value]) => (
                    <div key={key}>
                      <dt className="text-gray-500 dark:text-gray-400">{getBrokerFieldLabel(key)}</dt>
                      <dd className="text-gray-900 dark:text-white break-words">{formatFieldValue(key, value)}</dd>
                    </div>
                  ))}
                </dl>

                <div className="mt-8">
                  <Heading as="h4" size="xl" className="text-gray-900 dark:text-white mb-3">
                    Contatos
                  </Heading>
                  {detailData.contacts.length === 0 ? (
                    <Text size="sm" color="muted">Nenhum contato cadastrado.</Text>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full min-w-[640px] divide-y divide-gray-200 dark:divide-gray-700">
                        <thead className="bg-gray-50 dark:bg-gray-800">
                          <tr>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Nome</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Telefone</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">E-mail</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Cargo</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Departamento</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Sócio</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                          {detailData.contacts.map((contact: BrokerContact) => (
                            <tr key={contact.id}>
                              <td className="px-3 py-2 text-sm text-gray-900 dark:text-gray-100">{contact.name || '—'}</td>
                              <td className="px-3 py-2 text-sm text-gray-600 dark:text-gray-300">{contact.phone || '—'}</td>
                              <td className="px-3 py-2 text-sm text-gray-600 dark:text-gray-300">{contact.email || '—'}</td>
                              <td className="px-3 py-2 text-sm text-gray-600 dark:text-gray-300">{contact.role || '—'}</td>
                              <td className="px-3 py-2 text-sm text-gray-600 dark:text-gray-300">{contact.department || '—'}</td>
                              <td className="px-3 py-2 text-sm text-gray-600 dark:text-gray-300">
                                {contact.is_partner == null ? '—' : contact.is_partner ? 'Sim' : 'Não'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </>
            ) : null}

            <div className="mt-6 flex justify-end">
              <Button variant="outline" onClick={closeBrokerDetail}>
                Fechar
              </Button>
            </div>
          </div>
        </div>
      )}

      <PortaisAtendimentoCallQueueManageModal
        open={manageQueueModalOpen}
        onClose={() => setManageQueueModalOpen(false)}
        onQueueChanged={() => void refreshQueueBrokerIds()}
      />

      {addToQueueModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={closeAddToQueueModal}
          role="presentation"
        >
          <div
            className="bg-white dark:bg-gray-900 rounded-lg shadow-xl w-full max-w-lg p-6"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-labelledby="add-to-queue-modal-title"
            aria-modal="true"
          >
            <div className="flex items-start justify-between mb-4 gap-4">
              <Heading as="h3" size="2xl" id="add-to-queue-modal-title" className="text-gray-900 dark:text-white">
                Motivo da inclusão
              </Heading>
              <button
                type="button"
                onClick={closeAddToQueueModal}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                aria-label="Fechar"
                disabled={addingToQueue}
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            {addToQueueInfo ? (
              <Text size="sm" color="muted" className="mb-6">
                {addToQueueInfo}
              </Text>
            ) : (
              <>
                <Text size="sm" color="muted" className="mb-3">
                  Informe o motivo para incluir{' '}
                  {eligibleToAddBrokerIds.length === 1
                    ? '1 corretor'
                    : `${eligibleToAddBrokerIds.length} corretores`}{' '}
                  na fila de ligação.
                </Text>
                <textarea
                  value={addToQueueReason}
                  onChange={(e) => setAddToQueueReason(e.target.value)}
                  placeholder="Motivo..."
                  rows={4}
                  disabled={addingToQueue}
                  className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </>
            )}

            {addToQueueError && (
              <Text size="sm" className="text-red-600 dark:text-red-400 mt-3">
                {addToQueueError}
              </Text>
            )}

            <div className="mt-6 flex justify-end gap-3">
              <Button variant="outline" onClick={closeAddToQueueModal} disabled={addingToQueue}>
                {addToQueueInfo ? 'Fechar' : 'Cancelar'}
              </Button>
              {!addToQueueInfo && (
                <Button
                  variant="solid"
                  onClick={() => void handleAddToQueue()}
                  disabled={addingToQueue || !addToQueueReason.trim()}
                >
                  {addingToQueue ? 'Incluindo...' : 'Confirmar inclusão'}
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default PortaisAtendimentoGestaoFilaTab
